// signals-web.ts — 1:1 JS port of the derived halves of src-tauri/src/db.rs: PR detection
// (add_set), all-time PRs (list_prs), the AI-brain trend rows (trend_workouts) and the full
// TrendSignals aggregates + headline facts (trend_signals). 🔴 WHY: on web, every set stored
// is_pr:false, PRs listed empty, and the AI recap's signals were a zero shell (fleet zero-stub
// sweep 2026-08-19/20). Where this and the Rust could drift, the Rust wins.
//
// Same ceilings: deterministic, local, observational — facts state the user's own numbers,
// never advise or prescribe.

export interface WebSet {
  id: string; workout_id: string; exercise_slug: string; exercise_name: string;
  set_index: number; weight: number; reps: number; is_pr: boolean; pr_types: string[];
  notes: string | null; created_at: string;
}
export interface WebWorkout {
  id: string; name: string; performed_on: string; duration_minutes: number; created_at: string;
}

const round2 = (x: number) => Math.round(x * 100) / 100;
const roundHalf = (x: number) => Math.round(x * 2) / 2;

/** Epley 1RM (pr.dart heritage): reps==1 → weight; reps<=0 or weight<=0 → 0. */
export function estimateOneRepMax(weight: number, reps: number): number {
  if (reps <= 0 || weight <= 0) return 0;
  if (reps === 1) return weight;
  return weight * (1 + reps / 30);
}
const weightsEqual = (a: number, b: number) => Math.abs(a - b) < 0.005;

/** Movement-group classifier from the exercise NAME alone — 1:1 keyword order with the Rust. */
export function classifyGroup(name: string): string {
  const n = (name ?? "").toLowerCase();
  const has = (k: string) => n.includes(k);
  if (has("run") || has("treadmill") || has("cycl") || has("bike") || has("row")
    || has("elliptical") || has("cardio") || has("jog") || has("sprint")) {
    if (has("row") && (has("barbell") || has("cable") || has("pendlay") || has("t-bar") || has("seated"))) return "pull";
    if (has("row") && !has("rowing") && !has("rower")) return "pull";
    return "cardio";
  }
  if (has("squat") || has("deadlift") || has("lunge") || has("leg ") || has("leg-")
    || has("calf") || has("hip thrust") || has("hip-thrust") || has("glute")
    || has("split squat") || has("hamstring") || has("quad")) return "legs";
  if (has("row") || has("pull") || has("chin") || has("curl") || has("lat ")
    || has("pulldown") || has("face pull") || has("rear delt") || has("shrug") || has("bicep")) return "pull";
  if (has("bench") || has("press") || has("push") || has("dip") || has("fly")
    || has("pec") || has("tricep") || has("skull") || has("lateral raise")
    || has("front raise") || has("overhead") || has("arnold")) return "push";
  if (has("crunch") || has("plank") || has("ab ") || has("ab-") || has("twist")
    || has("leg raise") || has("rollout") || has("sit-up") || has("situp")) return "core";
  return "other";
}

function olsSlope(ys: number[]): number {
  const n = ys.length;
  if (n < 2) return 0;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (let i = 0; i < n; i++) { sumX += i; sumY += ys[i]; sumXY += i * ys[i]; sumXX += i * i; }
  const denom = n * sumXX - sumX * sumX;
  if (Math.abs(denom) < 1e-9) return 0;
  return (n * sumXY - sumX * sumY) / denom;
}
const directionFrom = (slope: number, avgMagnitude: number) => {
  const thresh = Math.max(Math.abs(avgMagnitude) * 0.03, 1e-6);
  return slope > thresh ? "increasing" : slope < -thresh ? "decreasing" : "stable";
};

const fmt0 = (x: number) => {
  const r = Math.round(x);
  return `${r < 0 ? "-" : ""}${Math.abs(r).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};
const fmtw = (x: number) => {
  const r = Math.round(x * 10) / 10;
  return Math.abs(r - Math.round(r)) < 1e-9 ? fmt0(r) : r.toFixed(1);
};

/** PR detection against PRIOR history for this exercise — 1:1 with db.rs::add_set. */
export function detectPr(
  history: { weight: number; reps: number; workout_id: string }[],
  input: { workout_id: string; weight: number; reps: number },
): { is_pr: boolean; pr_types: string[] } {
  let maxWeight = 0, maxOneRm = 0, repsAtCandidateWeight = 0;
  const sessionVol = new Map<string, number>();
  for (const h of history) {
    if (h.weight > maxWeight) maxWeight = h.weight;
    const orm = estimateOneRepMax(h.weight, h.reps);
    if (orm > maxOneRm) maxOneRm = orm;
    if (weightsEqual(h.weight, input.weight) && h.reps > repsAtCandidateWeight) repsAtCandidateWeight = h.reps;
    sessionVol.set(h.workout_id, (sessionVol.get(h.workout_id) ?? 0) + h.weight * h.reps);
  }
  const bestSessionVol = Math.max(0, ...sessionVol.values());
  const candidateOrm = estimateOneRepMax(input.weight, input.reps);
  const candidateSetVol = input.weight * input.reps;
  const candidateSessionVol = (sessionVol.get(input.workout_id) ?? 0) + candidateSetVol;

  const pr_types: string[] = [];
  if (input.weight > 0 || input.reps > 0) {
    if (input.weight > maxWeight && input.reps >= 1) pr_types.push("heaviest");
    if (input.reps > repsAtCandidateWeight && repsAtCandidateWeight > 0) pr_types.push("reps");
    if (candidateOrm > maxOneRm && candidateOrm > 0) pr_types.push("1RM");
    if (candidateSessionVol > bestSessionVol && candidateSetVol > 0) pr_types.push("volume");
  }
  return { is_pr: pr_types.length > 0, pr_types };
}

/** All-time PRs per exercise — 1:1 with db.rs::list_prs. */
export function listPrsWeb(sets: WebSet[], workouts: WebWorkout[]) {
  const dateOf = new Map(workouts.map((w) => [w.id, w.performed_on]));
  interface Agg { name: string; heaviest_weight: number; heaviest_weight_reps: number; estimated_one_rm: number; session_vol: Map<string, number>; performed_on: string }
  const by = new Map<string, Agg>();
  for (const s of sets) {
    if (!(s.weight > 0 && s.reps > 0)) continue;
    const a = by.get(s.exercise_slug) ?? { name: s.exercise_name, heaviest_weight: 0, heaviest_weight_reps: 0, estimated_one_rm: 0, session_vol: new Map(), performed_on: "" };
    if (s.weight > a.heaviest_weight) {
      a.heaviest_weight = s.weight;
      a.heaviest_weight_reps = s.reps;
      a.performed_on = dateOf.get(s.workout_id) ?? "";
    }
    const orm = estimateOneRepMax(s.weight, s.reps);
    if (orm > a.estimated_one_rm) a.estimated_one_rm = orm;
    a.session_vol.set(s.workout_id, (a.session_vol.get(s.workout_id) ?? 0) + s.weight * s.reps);
    by.set(s.exercise_slug, a);
  }
  return [...by.entries()]
    .map(([slug, a]) => ({
      exercise_slug: slug,
      exercise_name: a.name,
      heaviest_weight: a.heaviest_weight,
      heaviest_weight_reps: a.heaviest_weight_reps,
      estimated_one_rm: roundHalf(a.estimated_one_rm),
      best_session_volume: Math.max(0, ...a.session_vol.values()),
      performed_on: a.performed_on,
    }))
    .sort((x, y) => y.estimated_one_rm - x.estimated_one_rm);
}

/** Per-session progression for one exercise (best set by Epley 1RM per session, chronological).
 *  1:1 with db.rs::exercise_history — real on-device history for the progression chart. */
export function exerciseHistoryWeb(sets: WebSet[], workouts: WebWorkout[], slug: string) {
  const dateOf = new Map(workouts.map((w) => [w.id, w.performed_on]));
  const byDate = new Map<string, { top_weight: number; top_reps: number; est: number }>();
  for (const s of sets) {
    if (s.exercise_slug !== slug || !(s.weight > 0 && s.reps > 0)) continue;
    const date = dateOf.get(s.workout_id) ?? "";
    if (!date) continue;
    const e = estimateOneRepMax(s.weight, s.reps);
    const cur = byDate.get(date);
    if (!cur || e > cur.est) byDate.set(date, { top_weight: s.weight, top_reps: s.reps, est: e });
  }
  return [...byDate.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([performed_on, v]) => ({ performed_on, top_weight: v.top_weight, top_reps: v.top_reps, est_one_rm: roundHalf(v.est) }));
}

/** Last-N-days AI-brain rows — 1:1 with db.rs::trend_workouts. */
export function trendWorkoutsWeb(sets: WebSet[], workouts: WebWorkout[], days: number, todayIso: string) {
  const cutoff = new Date(Date.parse(`${todayIso}T00:00:00Z`) - days * 86400000).toISOString().slice(0, 10);
  const wmap = new Map(workouts.map((w) => [w.id, w]));
  const rows = sets
    .map((s) => ({ s, w: wmap.get(s.workout_id) }))
    .filter((x): x is { s: WebSet; w: WebWorkout } => !!x.w && x.w.performed_on >= cutoff)
    .sort((a, b) => a.w.performed_on.localeCompare(b.w.performed_on) || a.s.exercise_name.localeCompare(b.s.exercise_name) || a.s.set_index - b.s.set_index);
  const out: { date: string; exercise: string; sets: { weight: number; reps: number }[] }[] = [];
  for (const { s, w } of rows) {
    const date = w.performed_on.slice(0, 10);
    const last = out[out.length - 1];
    if (last && last.date === date && last.exercise === s.exercise_name) last.sets.push({ weight: s.weight, reps: s.reps });
    else out.push({ date, exercise: s.exercise_name, sets: [{ weight: s.weight, reps: s.reps }] });
  }
  return out;
}

/** The full TrendSignals — 1:1 with db.rs::trend_signals incl. headline facts. */
export function trendSignalsWeb(sets: WebSet[], workouts: WebWorkout[], days: number, todayIso: string) {
  const windowDays = days;
  const cutoff = new Date(Date.parse(`${todayIso}T00:00:00Z`) - days * 86400000).toISOString().slice(0, 10);
  const wmap = new Map(workouts.map((w) => [w.id, w]));
  const rows = sets
    .map((s) => ({ s, w: wmap.get(s.workout_id) }))
    .filter((x): x is { s: WebSet; w: WebWorkout } => !!x.w && x.w.performed_on >= cutoff)
    .sort((a, b) => a.w.performed_on.localeCompare(b.w.performed_on) || a.w.created_at.localeCompare(b.w.created_at) || a.s.set_index - b.s.set_index);

  interface Session { duration: number; volume: number; group_vol: Map<string, number>; ex_reps: Map<string, [number, number, number]>; ex_top: Map<string, number> }
  const order: string[] = [];
  const sessions = new Map<string, Session>();
  interface Lift { sessions: [string, number, number][]; current_date: string; current_top: number; current_1rm: number; prs: number }
  const lifts = new Map<string, Lift>();

  let totalSets = 0, totalVolume = 0, prsInWindow = 0;
  let dateFirst: string | null = null, dateLast: string | null = null;
  const daysSet = new Set<string>();

  for (const { s, w } of rows) {
    const date = w.performed_on.slice(0, 10);
    totalSets++;
    const vol = s.weight * s.reps;
    totalVolume += vol;
    if (s.is_pr) prsInWindow++;
    daysSet.add(date);
    if (dateFirst == null) dateFirst = date;
    dateLast = date;

    let sess = sessions.get(s.workout_id);
    if (!sess) {
      order.push(s.workout_id);
      sess = { duration: w.duration_minutes ?? 0, volume: 0, group_vol: new Map(), ex_reps: new Map(), ex_top: new Map() };
      sessions.set(s.workout_id, sess);
    }
    sess.volume += vol;
    const group = classifyGroup(s.exercise_name);
    sess.group_vol.set(group, (sess.group_vol.get(group) ?? 0) + (vol > 0 ? vol : 1));
    const er = sess.ex_reps.get(s.exercise_name);
    if (er) { er[1] = s.reps; er[2] += 1; } else sess.ex_reps.set(s.exercise_name, [s.reps, s.reps, 1]);
    sess.ex_top.set(s.exercise_name, Math.max(sess.ex_top.get(s.exercise_name) ?? 0, s.weight));

    let l = lifts.get(s.exercise_name);
    if (!l) { l = { sessions: [], current_date: date, current_top: 0, current_1rm: 0, prs: 0 }; lifts.set(s.exercise_name, l); }
    if (l.current_date !== date) {
      l.sessions.push([l.current_date, l.current_top, l.current_1rm]);
      l.current_date = date; l.current_top = 0; l.current_1rm = 0;
    }
    if (s.weight > l.current_top) l.current_top = s.weight;
    const orm = estimateOneRepMax(s.weight, s.reps);
    if (orm > l.current_1rm) l.current_1rm = orm;
    if (s.is_pr) l.prs++;
  }
  for (const l of lifts.values()) l.sessions.push([l.current_date, l.current_top, l.current_1rm]);

  const workoutsN = order.length;
  const daysTrained = daysSet.size;
  const spanDays = Math.max(days, 1);
  const frequency = {
    sessions_per_week: round2((workoutsN / spanDays) * 7),
    days_trained: daysTrained,
    rest_days: Math.max(spanDays - daysTrained, 0),
  };

  const volSeries = order.map((id) => round2(sessions.get(id)!.volume));
  const volAvg = volSeries.length ? volSeries.reduce((s, x) => s + x, 0) / volSeries.length : 0;
  const volSlope = round2(olsSlope(volSeries));
  const firstVol = volSeries[0] ?? 0;
  const lastVol = volSeries[volSeries.length - 1] ?? 0;
  const volume = {
    per_session: volSeries,
    slope_per_session: volSlope,
    direction: directionFrom(volSlope, volAvg),
    first_session: firstVol,
    last_session: lastVol,
    pct_change: firstVol > 0 ? round2(((lastVol - firstVol) / firstVol) * 100) : null,
  };

  const intensitySeries: number[] = [];
  for (const id of order) {
    const s = sessions.get(id)!;
    if (s.duration > 0) intensitySeries.push(round2(s.volume / s.duration));
  }
  let intensity: Record<string, unknown> | null = null;
  if (intensitySeries.length >= 2) {
    const avg = intensitySeries.reduce((s, x) => s + x, 0) / intensitySeries.length;
    const slope = olsSlope(intensitySeries);
    intensity = {
      first: intensitySeries[0], last: intensitySeries[intensitySeries.length - 1],
      avg: round2(avg), direction: directionFrom(slope, avg),
      sessions_with_duration: intensitySeries.length, per_session: intensitySeries,
    };
  }

  const sessionGroups = order.map((id) => {
    let best = "other", bestV = -Infinity;
    for (const [g, v] of sessions.get(id)!.group_vol) if (v > bestV) { best = g; bestV = v; }
    return best;
  });
  const groupCounts = new Map<string, number>();
  for (const g of sessionGroups) groupCounts.set(g, (groupCounts.get(g) ?? 0) + 1);
  let mostCommonGroup: string | null = null, mcN = 0;
  for (const [g, c] of groupCounts) if (c > mcN) { mostCommonGroup = g; mcN = c; }
  const alternates = sessionGroups.length >= 2 && sessionGroups.every((g, i) => i === 0 || g !== sessionGroups[i - 1]);
  const hasG = (g: string) => sessionGroups.includes(g);
  const split = {
    session_groups: sessionGroups,
    distinct_groups: groupCounts.size,
    alternates,
    most_common_group: mostCommonGroup,
    looks_like_ppl: hasG("push") && hasG("pull") && hasG("legs"),
  };

  let measured = 0, declining = 0, dropSum = 0, maxDrop = 0;
  let exampleExercise: string | null = null;
  for (const id of order) {
    for (const [ex, [first, last, setCount]] of sessions.get(id)!.ex_reps) {
      if (setCount < 2 || (first <= 0 && last <= 0)) continue;
      measured++;
      const drop = first - last;
      if (drop > 0) {
        declining++;
        dropSum += drop;
        if (drop > maxDrop) { maxDrop = drop; exampleExercise = ex; }
      }
    }
  }
  const set_fatigue = {
    exercises_measured: measured,
    exercises_declining: declining,
    avg_reps_drop: declining > 0 ? round2(dropSum / declining) : 0,
    max_reps_drop: maxDrop,
    example_exercise: exampleExercise,
  };

  const per_lift = [...lifts.entries()]
    .filter(([, l]) => l.sessions.length > 0)
    .map(([ex, l]) => {
      const tops = l.sessions.map((s) => s[1]);
      const avgTop = tops.length ? tops.reduce((s, x) => s + x, 0) / tops.length : 0;
      return {
        exercise: ex,
        sessions: l.sessions.length,
        first_top_set: round2(l.sessions[0][1]),
        last_top_set: round2(l.sessions[l.sessions.length - 1][1]),
        best_est_1rm: roundHalf(Math.max(0, ...l.sessions.map((s) => s[2]))),
        direction: directionFrom(olsSlope(tops), avgTop),
        prs: l.prs,
        history: l.sessions.map((s) => [s[0], round2(s[1])] as [string, number]),
      };
    })
    .sort((a, b) => b.sessions - a.sessions || b.best_est_1rm - a.best_est_1rm)
    .slice(0, 8);

  // ── headline facts (sentences 1:1 with the Rust) ──
  const facts: { kind: string; text: string; tone: string }[] = [];
  if (workoutsN > 0) {
    if (prsInWindow > 0) {
      const withPrs = per_lift.filter((l) => l.prs > 0);
      const topPrLift = withPrs.length ? withPrs.reduce((a, b) => (b.prs > a.prs ? b : a)) : null;
      const s = prsInWindow !== 1 ? "s" : "";
      const text = topPrLift && topPrLift.prs === prsInWindow
        ? `You set ${prsInWindow} personal record${s} in the last ${windowDays} days, all on ${topPrLift.exercise}.`
        : topPrLift
        ? `You set ${prsInWindow} personal record${s} in the last ${windowDays} days — the most (${topPrLift.prs}) on ${topPrLift.exercise}.`
        : `You set ${prsInWindow} personal record${s} in the last ${windowDays} days.`;
      facts.push({ kind: "pr", text, tone: "good" });
    }
    const pct = volume.pct_change;
    if (volume.direction === "increasing" && pct != null && Math.abs(pct) >= 1) {
      facts.push({ kind: "volume", tone: "good", text: `Your session volume is trending up — ${fmtw(volume.last_session)} in your latest session versus ${fmtw(volume.first_session)} in your first, about ${Math.abs(pct).toFixed(0)}% higher.` });
    } else if (volume.direction === "decreasing" && pct != null && Math.abs(pct) >= 1) {
      facts.push({ kind: "volume", tone: "watch", text: `Your session volume is trending down — ${fmtw(volume.last_session)} in your latest session versus ${fmtw(volume.first_session)} in your first, about ${Math.abs(pct).toFixed(0)}% lower.` });
    } else if (totalVolume > 0 && workoutsN >= 2) {
      facts.push({ kind: "volume", tone: "neutral", text: `Your per-session volume has held steady, averaging about ${fmtw(totalVolume / workoutsN)} across ${workoutsN} sessions.` });
    }
    const movers = per_lift.filter((l) => l.sessions >= 2 && l.direction !== "stable" && l.first_top_set > 0);
    if (movers.length) {
      const l = movers.reduce((a, b) => (Math.abs(b.last_top_set - b.first_top_set) > Math.abs(a.last_top_set - a.first_top_set) ? b : a));
      const delta = l.last_top_set - l.first_top_set;
      const pctL = l.first_top_set > 0 ? Math.abs((delta / l.first_top_set) * 100) : 0;
      const [word, tone] = delta > 0 ? ["climbed", "good"] : ["eased back", "watch"];
      facts.push({ kind: "lift", tone, text: `Your top set on ${l.exercise} has ${word} from ${fmtw(l.first_top_set)} to ${fmtw(l.last_top_set)} over ${l.sessions} sessions (${pctL.toFixed(0)}%).` });
    }
    const strongest = per_lift.filter((l) => l.best_est_1rm > 0);
    if (strongest.length) {
      const l = strongest.reduce((a, b) => (b.best_est_1rm > a.best_est_1rm ? b : a));
      facts.push({ kind: "lift", tone: "neutral", text: `Your top estimated 1-rep max this window is about ${fmtw(l.best_est_1rm)} on ${l.exercise}.` });
    }
    facts.push({ kind: "frequency", tone: "neutral", text: `You trained on ${daysTrained} of the last ${windowDays} days — about ${frequency.sessions_per_week.toFixed(1)} session${Math.abs(frequency.sessions_per_week - 1) < 1e-9 ? "" : "s"} a week, with ${frequency.rest_days} rest day${frequency.rest_days !== 1 ? "s" : ""}.` });
    if (sessionGroups.length >= 2) {
      if (alternates) {
        facts.push({ kind: "split", tone: "good", text: `Your sessions alternated muscle groups cleanly — ${sessionGroups.join(" → ")}${split.looks_like_ppl ? ", a push/pull/legs rhythm" : ""}.` });
      } else if (mostCommonGroup) {
        const same = sessionGroups.filter((x) => x === mostCommonGroup).length;
        if (same >= 2 && split.distinct_groups <= 2) {
          facts.push({ kind: "split", tone: "watch", text: `${same} of your ${sessionGroups.length} sessions led with ${mostCommonGroup} work — your training leaned heavily one way.` });
        }
      }
    }
    if (set_fatigue.exercises_measured > 0 && set_fatigue.exercises_declining > 0) {
      let text = `Reps faded across sets on ${set_fatigue.exercises_declining} of ${set_fatigue.exercises_measured} multi-set lifts — about ${fmtw(set_fatigue.avg_reps_drop)} fewer rep${Math.abs(set_fatigue.avg_reps_drop - 1) < 1e-9 ? "" : "s"} from first set to last.`;
      if (exampleExercise && maxDrop > 0) text += ` The steepest drop was ${maxDrop} on ${exampleExercise}.`;
      facts.push({ kind: "fatigue", text, tone: "watch" });
    }
    if (intensity && (intensity.sessions_with_duration as number) >= 2 && intensity.direction !== "stable") {
      const [word, tone] = intensity.direction === "increasing" ? ["denser", "good"] : ["lighter", "watch"];
      facts.push({ kind: "intensity", tone, text: `Your timed sessions got ${word} — volume per minute moved from ${fmtw(intensity.first as number)} to ${fmtw(intensity.last as number)} across ${intensity.sessions_with_duration} workouts.` });
    }
    if (totalSets > 0 && totalVolume > 0) {
      facts.push({ kind: "milestone", tone: "neutral", text: `Across the window you logged ${fmt0(totalSets)} sets totalling ${fmt0(totalVolume)} of volume.` });
    }
  }

  return {
    window_days: windowDays,
    workouts: workoutsN,
    total_sets: totalSets,
    total_volume: round2(totalVolume),
    date_first: dateFirst,
    date_last: dateLast,
    frequency,
    volume,
    intensity,
    split,
    set_fatigue,
    per_lift,
    prs_in_window: prsInWindow,
    headline_facts: facts,
  };
}
